import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import {
    createUser,
    deleteUser,
    getRegistrationStatus,
    getUsers,
    resetUserPassword,
    updateRegistrationStatus,
    updateUserRole,
} from '../../utils/api';
import { UserRole } from '../../common/types';

type UserRow = {
    id: number;
    username: string;
    role: UserRole;
    created_at?: string;
    mustChangePassword?: boolean;
};

const roleLabels: Record<UserRole, string> = {
    superadmin: 'Superadmin',
    admin: 'Admin',
    copy_editor: 'Copy Editor',
    viewer: 'Viewer',
};

const emptyResetForm = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    reason: '',
    requireChange: true,
};

const extractMessage = (error: unknown, fallback: string) => {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message?: unknown }).message || fallback);
    }
    return fallback;
};

const UserManagementPage: React.FC = () => {
    const { user } = useContext(AuthContext);
    const isSuperadmin = user?.role === 'superadmin';
    const [users, setUsers] = useState<UserRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<string | null>(null);
    const [newUser, setNewUser] = useState<{
        username: string;
        password: string;
        role: UserRole;
        requireChange: boolean;
    }>({ username: '', password: '', role: 'viewer', requireChange: true });
    const [registrationLocked, setRegistrationLocked] = useState(false);
    const [savingUser, setSavingUser] = useState(false);
    const [registrationSaving, setRegistrationSaving] = useState(false);
    const [deletingUserIds, setDeletingUserIds] = useState<number[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
    const [resetForm, setResetForm] = useState(emptyResetForm);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);

    useEffect(() => {
        if (!isSuperadmin) {
            setLoading(false);
            return;
        }
        async function load() {
            try {
                const [usersResponse, registrationResponse] = await Promise.all([getUsers(), getRegistrationStatus()]);
                setUsers(usersResponse.data || []);
                setRegistrationLocked(Boolean(registrationResponse?.locked));
            } catch (error) {
                setMessage(extractMessage(error, 'Failed to load users or registration settings.'));
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [isSuperadmin]);

    const handleRoleChange = async (id: number, newRole: UserRole) => {
        setMessage(null);
        try {
            await updateUserRole(id, newRole);
            setUsers((previous) => previous.map((account) => (
                account.id === id ? { ...account, role: newRole } : account
            )));
            setMessage('Role updated.');
        } catch (error) {
            setMessage(extractMessage(error, 'Failed to update role.'));
        }
    };

    const handleAddUser = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setMessage(null);
        if (!newUser.username.trim() || !newUser.password) {
            setMessage('Username and temporary password are required.');
            return;
        }
        setSavingUser(true);
        try {
            const created = await createUser(
                newUser.username.trim(),
                newUser.password,
                newUser.role,
                newUser.requireChange,
            );
            setUsers((previous) => [{
                id: created.id,
                username: created.username,
                role: created.role,
                created_at: created.created_at,
                mustChangePassword: created.mustChangePassword,
            }, ...previous]);
            setNewUser({ username: '', password: '', role: 'viewer', requireChange: true });
            setMessage('User created. Share the temporary password privately.');
        } catch (error) {
            setMessage(extractMessage(error, 'Failed to create user.'));
        } finally {
            setSavingUser(false);
        }
    };

    const handleDeleteUser = async (id: number) => {
        setMessage(null);
        const confirmed = window.confirm('Remove this user account? This cannot be undone.');
        if (!confirmed) return;
        setDeletingUserIds((previous) => [...previous, id]);
        try {
            await deleteUser(id);
            setUsers((previous) => previous.filter((account) => account.id !== id));
            setMessage('User removed.');
        } catch (error) {
            setMessage(extractMessage(error, 'Failed to remove user.'));
        } finally {
            setDeletingUserIds((previous) => previous.filter((userId) => userId !== id));
        }
    };

    const handleRegistrationToggle = async () => {
        setMessage(null);
        setRegistrationSaving(true);
        try {
            const nextLocked = !registrationLocked;
            await updateRegistrationStatus(nextLocked);
            setRegistrationLocked(nextLocked);
            setMessage(nextLocked ? 'Registration locked.' : 'Registration unlocked.');
        } catch (error) {
            setMessage(extractMessage(error, 'Failed to update registration lock.'));
        } finally {
            setRegistrationSaving(false);
        }
    };

    const openPasswordReset = (account: UserRow) => {
        setSelectedUser(account);
        setResetForm(emptyResetForm);
        setResetError(null);
    };

    const closePasswordReset = () => {
        if (resettingPassword) return;
        setSelectedUser(null);
        setResetForm(emptyResetForm);
        setResetError(null);
    };

    const handlePasswordReset = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedUser) return;
        setResetError(null);
        if (resetForm.newPassword !== resetForm.confirmPassword) {
            setResetError('The temporary passwords do not match.');
            return;
        }
        setResettingPassword(true);
        try {
            const response = await resetUserPassword(selectedUser.id, resetForm);
            setUsers((previous) => previous.map((account) => (
                account.id === selectedUser.id
                    ? { ...account, mustChangePassword: response.mustChangePassword }
                    : account
            )));
            setMessage(
                resetForm.requireChange
                    ? `Password reset for ${selectedUser.username}. They must choose a new password at next sign-in.`
                    : `Password updated for ${selectedUser.username}. Their existing sign-ins have ended.`,
            );
            setSelectedUser(null);
            setResetForm(emptyResetForm);
        } catch (error) {
            setResetError(extractMessage(error, 'We could not reset this password.'));
        } finally {
            setResettingPassword(false);
        }
    };

    if (!isSuperadmin) {
        return (
            <main className="ml-64 p-6">
                <h1 className="mb-2 text-2xl font-semibold">User Management</h1>
                <p className="text-gray-600">Superadmins only. Your account does not have access.</p>
            </main>
        );
    }

    return (
        <main className="ml-64 min-h-screen bg-gray-50 px-6 py-8">
            <div className="max-w-6xl">
                <p className="text-sm font-semibold text-ekc-blue">CMS access</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-gray-950">User Management</h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
                    Create accounts, choose what each person can access, and safely reset a forgotten password. Passwords are never shown after they are saved.
                </p>

                {message && <div role="status" className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">{message}</div>}

                {loading ? (
                    <div className="mt-8 text-gray-600">Loading users…</div>
                ) : (
                    <>
                        <section aria-labelledby="account-settings-heading" className="mt-8 grid gap-6 border-y border-gray-200 py-6 lg:grid-cols-2">
                            <div>
                                <h2 id="account-settings-heading" className="text-lg font-semibold text-gray-950">Registration</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {registrationLocked
                                        ? 'New self-registration is locked. Superadmins can still create accounts here.'
                                        : 'Registration is open to people who have the separate registration key.'}
                                </p>
                                <button type="button" className={`mt-4 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${registrationLocked ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-700 hover:bg-emerald-800'} disabled:cursor-not-allowed disabled:opacity-60`} onClick={handleRegistrationToggle} disabled={registrationSaving}>
                                    {registrationSaving ? 'Saving…' : registrationLocked ? 'Unlock registration' : 'Lock registration'}
                                </button>
                            </div>

                            <div className="lg:border-l lg:border-gray-200 lg:pl-6">
                                <h2 className="text-lg font-semibold text-gray-950">Add a user</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">Create a temporary password and share it privately.</p>
                                <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleAddUser}>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700" htmlFor="newUsername">Username</label>
                                        <input id="newUsername" type="text" autoComplete="off" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" value={newUser.username} onChange={(event) => setNewUser({ ...newUser, username: event.target.value })} required />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700" htmlFor="newPassword">Temporary password</label>
                                        <input id="newPassword" type="password" autoComplete="new-password" minLength={12} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" value={newUser.password} onChange={(event) => setNewUser({ ...newUser, password: event.target.value })} required />
                                        <p className="mt-1 text-xs text-gray-500">At least 12 characters; do not include the username.</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700" htmlFor="newRole">Role</label>
                                        <select id="newRole" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" value={newUser.role} onChange={(event) => setNewUser({ ...newUser, role: event.target.value as UserRole })}>
                                            {Object.keys(roleLabels).map((roleKey) => <option key={roleKey} value={roleKey}>{roleLabels[roleKey as UserRole]}</option>)}
                                        </select>
                                    </div>
                                    <label className="flex items-start gap-2 text-sm text-gray-700 sm:pt-7">
                                        <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-ekc-blue focus:ring-ekc-blue" checked={newUser.requireChange} onChange={(event) => setNewUser({ ...newUser, requireChange: event.target.checked })} />
                                        Require a private password at first sign-in
                                    </label>
                                    <button type="submit" className="justify-center rounded-md bg-ekc-blue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-85 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2" disabled={savingUser}>
                                        {savingUser ? 'Creating…' : 'Create user'}
                                    </button>
                                </form>
                            </div>
                        </section>

                        <section aria-labelledby="user-accounts-heading" className="mt-8">
                            <div className="flex flex-wrap items-end justify-between gap-3">
                                <div>
                                    <h2 id="user-accounts-heading" className="text-xl font-semibold text-gray-950">User accounts</h2>
                                    <p className="mt-1 text-sm text-gray-600">A password reset ends that person’s existing CMS sign-ins.</p>
                                </div>
                                <Link to="/dashboard/account" className="text-sm font-semibold text-ekc-blue underline decoration-blue-300 underline-offset-4 hover:decoration-ekc-blue">Change your password</Link>
                            </div>

                            <div className="mt-4 overflow-x-auto border-y border-gray-200 bg-white">
                                <table className="min-w-full text-left">
                                    <thead className="border-b border-gray-200 bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-sm font-semibold text-gray-700">Username</th>
                                            <th className="px-4 py-3 text-sm font-semibold text-gray-700">Role</th>
                                            <th className="px-4 py-3 text-sm font-semibold text-gray-700">Password status</th>
                                            <th className="px-4 py-3 text-sm font-semibold text-gray-700">Created</th>
                                            <th className="px-4 py-3 text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((account) => {
                                            const isCurrentUser = account.id === user?.id;
                                            const isDeleting = deletingUserIds.includes(account.id);
                                            return (
                                                <tr key={account.id} className="border-b border-gray-100 last:border-none">
                                                    <td className="px-4 py-3 font-medium text-gray-950">{account.username}{isCurrentUser && <span className="ml-2 text-xs font-normal text-gray-500">You</span>}</td>
                                                    <td className="px-4 py-3">
                                                        {isCurrentUser ? (
                                                            <span className="text-sm text-gray-700">{roleLabels[account.role]}</span>
                                                        ) : (
                                                            <select className="rounded-md border border-gray-300 px-2 py-1 text-sm" value={account.role} onChange={(event) => handleRoleChange(account.id, event.target.value as UserRole)}>
                                                                {Object.keys(roleLabels).map((roleKey) => <option key={roleKey} value={roleKey}>{roleLabels[roleKey as UserRole]}</option>)}
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {account.mustChangePassword
                                                            ? <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">Change required</span>
                                                            : <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">Ready</span>}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">{account.created_at ? new Date(account.created_at).toLocaleString() : '—'}</td>
                                                    <td className="px-4 py-3">
                                                        {isCurrentUser ? (
                                                            <Link to="/dashboard/account" className="text-sm font-semibold text-ekc-blue hover:underline">Update mine</Link>
                                                        ) : (
                                                            <div className="flex flex-wrap items-center gap-4">
                                                                <button type="button" className="text-sm font-semibold text-ekc-blue hover:underline" onClick={() => openPasswordReset(account)}>Reset password</button>
                                                                <button type="button" className="text-sm font-semibold text-red-600 hover:underline disabled:opacity-60" onClick={() => handleDeleteUser(account.id)} disabled={isDeleting}>{isDeleting ? 'Removing…' : 'Remove'}</button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>
                )}
            </div>

            <Transition.Root show={Boolean(selectedUser)} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closePasswordReset}>
                    <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-gray-950/35" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 translate-y-2" enterTo="opacity-100 translate-y-0" leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-2">
                                <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                                    <Dialog.Title className="text-xl font-semibold text-gray-950">Reset password for {selectedUser?.username}</Dialog.Title>
                                    <p className="mt-2 text-sm leading-6 text-gray-600">This ends the person’s existing CMS sign-ins. Share the temporary password through a private channel.</p>
                                    {resetError && <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{resetError}</div>}
                                    <form onSubmit={handlePasswordReset} className="mt-5 space-y-4">
                                        <div>
                                            <label htmlFor="resetNewPassword" className="block text-sm font-semibold text-gray-900">Temporary password</label>
                                            <input id="resetNewPassword" type="password" autoComplete="new-password" minLength={12} value={resetForm.newPassword} onChange={(event) => setResetForm({ ...resetForm, newPassword: event.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2.5" required />
                                            <p className="mt-1 text-xs text-gray-500">At least 12 characters; do not include the username.</p>
                                        </div>
                                        <div>
                                            <label htmlFor="resetConfirmPassword" className="block text-sm font-semibold text-gray-900">Confirm temporary password</label>
                                            <input id="resetConfirmPassword" type="password" autoComplete="new-password" minLength={12} value={resetForm.confirmPassword} onChange={(event) => setResetForm({ ...resetForm, confirmPassword: event.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2.5" required />
                                        </div>
                                        <div>
                                            <label htmlFor="resetReason" className="block text-sm font-semibold text-gray-900">Reason</label>
                                            <input id="resetReason" type="text" maxLength={250} value={resetForm.reason} onChange={(event) => setResetForm({ ...resetForm, reason: event.target.value })} placeholder="For example: User forgot their password" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2.5" required />
                                            <p className="mt-1 text-xs text-gray-500">Saved in the activity log. The password itself is never recorded.</p>
                                        </div>
                                        <div>
                                            <label htmlFor="adminCurrentPassword" className="block text-sm font-semibold text-gray-900">Your current password</label>
                                            <input id="adminCurrentPassword" type="password" autoComplete="current-password" value={resetForm.currentPassword} onChange={(event) => setResetForm({ ...resetForm, currentPassword: event.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2.5" required />
                                            <p className="mt-1 text-xs text-gray-500">Confirms that it is really you making this change.</p>
                                        </div>
                                        <label className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-3 text-sm text-amber-950">
                                            <input type="checkbox" className="mt-0.5 rounded border-amber-300 text-ekc-blue focus:ring-ekc-blue" checked={resetForm.requireChange} onChange={(event) => setResetForm({ ...resetForm, requireChange: event.target.checked })} />
                                            <span><strong className="font-semibold">Require a private password at next sign-in</strong><br />Recommended for forgotten-password resets.</span>
                                        </label>
                                        <div className="flex justify-end gap-3 pt-2">
                                            <button type="button" onClick={closePasswordReset} disabled={resettingPassword} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">Cancel</button>
                                            <button type="submit" disabled={resettingPassword} className="rounded-md bg-ekc-blue px-4 py-2 text-sm font-semibold text-white hover:bg-opacity-85 disabled:opacity-60">{resettingPassword ? 'Resetting…' : 'Reset password'}</button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </main>
    );
};

export default UserManagementPage;
